import { useEffect, useCallback, useRef, useState } from 'react'
import { useActions, useSelector, useStore } from 'statux'
import { Link, useLocation } from 'react-router-dom'
import Tippy, { useSingleton } from '@tippyjs/react'
import { tabbable } from 'tabbable'

import Image from '../Image/Image'
import FounderTracRegistration from '../FounderTracRegistration/FounderTracRegistration'

import { getCssVar, setCssVar } from '../../utils/css'
import { scrollTo, setSiblingsAriaHidden } from '../../utils/dom'

import useEventListener from '../../hooks/useEventListener'
import useScrollLockToggle from '../../hooks/useScrollLockToggle'
import useWindowSize from '../../hooks/useWindowSize'

import authProvider from '../../utils/auth-provider'

export default function GlobalHeader (props = {
    className: null,
    headerRef: null,
    routeType: null,
    stickyCta: null,
    style: null
}) {
    // Route Detection
    const { pathname } = useLocation()

    const isDashboard = props.routeType === 'Dashboard'

    // Global modal
    const setModal = useActions('modal')

    // User
    const [user, setUser] = useStore('user')

    const [userMenuPrimaryToggled, setUserMenuPrimaryToggled] = useState(false)

    const [userMenuStickyToggled, setUserMenuStickyToggled] = useState(false)

    const foundertracAccessToken = useSelector('foundertracAccessToken')

    const advancedMembershipAt = useSelector('advancedMembershipAt')

    // User Menu Refs
    const userMenuPrimaryToggle = useRef()

    const userMenuPrimary = useRef()

    const userMenuStickyToggle = useRef()

    const userMenuSticky = useRef()

    // Sticky Refs
    const stickyNav = useRef()

    const stickyBar = useRef()

    const stickyOverlayToggle = useRef()

    // Refs
    const dashboardAside = useRef()

    const header = useRef()

    // Tooltips
    const [tippySource, tippyTarget] = useSingleton()

    // Header ref
    const onHeaderRefCallback = (ref) => {
        header.current = ref

        if (typeof props.headerRef === 'function')
            props.headerRef(ref)

        if ('current' in props.headerRef)
            props.headerRef.current = ref
    }

    // Sticky Nav Offset
    const getStickyNavOffset = () => {
        const offsetTop = ((header.current || {})).offsetTop || 0
        const clientHeight = (header.current || {}).clientHeight || 0

        const offset = window.innerWidth < 1024
                || isDashboard
            ? offsetTop || 1
            : (offsetTop + clientHeight) - 25

        return offset > 0
            ? offset
            : 0
    }

    // Trigger render on window size changes
    const size = useWindowSize()

    // Scroll Lock hook
    const [scrollLock, toggleScrollLock] = useScrollLockToggle()

    // Sticky CTA
    const stickyCtaText = (props.stickyCta || {}).text || ''

    const stickyCtaTextWords = stickyCtaText
        .split(/\s+/)
        .filter(str => str)

    const stickyCtaTextSm = stickyCtaTextWords.length === 1
        ? stickyCtaText
        : ((stickyCtaTextWords[0] || '').match(/^(apply|register|join)$/i)
            ? stickyCtaTextWords[0]
            : (stickyCtaText.match(/^sign\s+up/i)
                ? stickyCtaText.replace(/^(sign\s+up).+/i, '$1')
                : null))

    // Sticky via Global Store
    const { sticky, stickyDisabled, stickyOverlay, dashboardPath }
        = useSelector('globalHeader')

    const setSticky = useActions('globalHeader.sticky')

    const setStickyOverlay = useActions('globalHeader.stickyOverlay')

    // Sticky State Handler
    const setStickyState = () => {
        if (document.documentElement.classList.contains('scroll-lock-force'))
            return

        if (stickyDisabled)
            return sticky
                && setSticky(false)

        const isSticky = window.scrollY > getStickyNavOffset()

        if (isSticky
                && !sticky
                && userMenuPrimaryToggled)
            setUserMenuPrimaryToggled(false)

        if (!isSticky
                && sticky
                && userMenuStickyToggled
                && window.innerWidth >= 1024)
            setUserMenuStickyToggled(false)

        if ((isSticky
                    && !sticky)
                || (!isSticky
                    && sticky))
            setSticky(isSticky)
    }

    // Sticky State
    useEffect(() => {
        setStickyState()
        window.addEventListener('scroll', setStickyState)
        return () => window.removeEventListener('scroll', setStickyState)
    }, [
        size,
        sticky,
        stickyDisabled,
        userMenuPrimaryToggled,
        userMenuStickyToggled
    ])

    // Sticky CSS Vars
    useEffect(() => {
        if (stickyOverlay)
            return window.innerWidth >= 1024
                && setStickyOverlay(false)

        // Set Sticky CSS Vars
        const stickyHeight = `${((stickyBar.current
            || document.getElementById('global-header-sticky-bar')
            || {}).clientHeight || 0)}px`

        if (stickyHeight !== getCssVar('--sticky-nav-height'))
            setCssVar('--sticky-nav-height', stickyHeight)

        const stickyOffset = (!stickyDisabled
                    || isDashboard)
                && getStickyNavOffset() > 0
            ? `${getStickyNavOffset() + 1}px`
            : '0px'

        if (stickyOffset !== getCssVar('--sticky-nav-offset'))
            setCssVar('--sticky-nav-offset', stickyOffset)

        // Set Dashboard Aside CSS Vars
        const dashboardAsideWidth = window.innerWidth < 1024
            ? '0px'
            : `${((dashboardAside.current
                || document.getElementById('global-header-dashboard-aside')
                || {}).clientWidth || 0)}px`

        if (dashboardAsideWidth !== getCssVar('--dashboard-aside-width'))
            setCssVar('--dashboard-aside-width', dashboardAsideWidth)
    }, [size, sticky, stickyDisabled, isDashboard, useSelector('alert')])

    // Stick Overlay Toggling
    useEffect(() => {
        const isShow = stickyOverlay
            && !scrollLock

        const isHide = !stickyOverlay
            && scrollLock

        if (!(isShow || isHide))
            return

        const scrollToCoords = [0, 0]

        if (isShow)
            scrollToCoords[1] = !sticky
                ? getStickyNavOffset() + 1
                : window.scrollY

        const scrollToOpts = {
            cancelOnUserAction: false,
            elementToScroll: isShow
                ? window
                : stickyNav.current
        }

        scrollTo(scrollToCoords, scrollToOpts)
            .then(() => {
                setSiblingsAriaHidden(
                    header.current || document.getElementById('global-header'),
                    isShow)

                if (isHide
                        && stickyBar.current
                        && !stickyBar.current
                            .contains(document.activeElement)
                        && stickyOverlayToggle.current)
                    stickyOverlayToggle.current
                        .focus({ preventScroll: true })

                toggleScrollLock()
            })
    }, [stickyOverlay])

    // Tab trap - User Menu & Sticky Overlay
    useEventListener('keydown', useCallback((e) => {
        const isUserMenu = userMenuStickyToggled
            && window.innerWidth < 1024

        if (!scrollLock
                || !(isUserMenu || stickyOverlay))
            return

        // On Escape
        if (e.key.match(/escape/i))
            isUserMenu
                ? setUserMenuStickyToggled(false)
                : setStickyOverlay(false)

        // Trap Tab keypresses
        if (!e.key.match(/tab/i))
            return

        let activeEl

        const el = isUserMenu
            ? userMenuSticky.current
                || document.querySelector(
                    '#global-header-sticky .global-header-user-menu')
            : stickyNav.current
                || document.getElementById('global-header-sticky')

        const els = tabbable(el) || []

        if (e.shiftKey
                && els.indexOf(e.target) === 0)
            activeEl = els[els.length - 1]

        if (!e.shiftKey
                && els.indexOf(e.target) === els.length - 1)
            activeEl = els[0]

        if (!activeEl)
            return

        e.preventDefault()
        e.stopPropagation()

        activeEl.focus()
    }, [scrollLock, userMenuStickyToggled]))

    // User Menu & Toggle
    const userMenuOnBlur = (e) => {
        if (!userMenuPrimaryToggled
                && !userMenuStickyToggled)
            return

        const isPrimaryToggled = userMenuPrimaryToggled

        const activeRef = isPrimaryToggled
            ? userMenuPrimary.current
            : userMenuSticky.current

        if (!activeRef)
            return

        const activeToggleRef = isPrimaryToggled
            ? userMenuPrimaryToggle.current
            : userMenuStickyToggle.current

        if (e.target === activeRef
                || activeRef.contains(e.target)
                || (activeToggleRef
                    && e.target === activeToggleRef
                    && window.innerWidth >= 1024))
            return

        const setActiveToggledState = isPrimaryToggled
            ? setUserMenuPrimaryToggled
            : setUserMenuStickyToggled

        setActiveToggledState(false)
    }

    useEventListener('click', useCallback((e) =>
        userMenuOnBlur(e), [userMenuPrimaryToggled, userMenuStickyToggled]))

    useEventListener('focusin', useCallback((e) =>
        userMenuOnBlur(e), [userMenuPrimaryToggled, userMenuStickyToggled]))

    // Resizes
    useEffect(() => {
        if (window.innerWidth < 1024
                && userMenuPrimaryToggled)
            setUserMenuPrimaryToggled(false)

        if (window.innerWidth < 1024
                && userMenuStickyToggled
                && !scrollLock)
            setUserMenuStickyToggled(false)

        if (window.innerWidth >= 1024
                && userMenuStickyToggled
                && scrollLock) {
            setUserMenuStickyToggled(false)

            const globalHeader = header.current
                || document.getElementById('global-header')

            if (globalHeader.getAttribute('aria-hidden') !== 'true')
                toggleScrollLock()
        }
    }, [size])

    // On Off-Canvas Toggle
    useEffect(() => {
        const globalHeader = header.current
            || document.getElementById('global-header')

        if (window.innerWidth < 1024) {
            const isStickyOverlay = stickyOverlay && scrollLock

            setSiblingsAriaHidden(globalHeader,
                userMenuStickyToggled
                    || isStickyOverlay)

            if (scrollLock
                    && !userMenuStickyToggled
                    && !isStickyOverlay
                    && globalHeader.getAttribute('aria-hidden') !== 'true')
                toggleScrollLock()
        }
    }, [userMenuStickyToggled])

    useEffect(() => {
        const onPopstate = (e) => {
            if (window.innerWidth > 1023)
                return

            if (stickyOverlay)
                setStickyOverlay(false)

            if (userMenuStickyToggled)
                setUserMenuStickyToggled(false)
        }

        window.addEventListener('popstate', onPopstate)

        return () => window.removeEventListener('popstate', onPopstate)
    }, [])

    const renderUser = (
        idPrefix = null,
        toggleRef = null,
        menuRef = null,
        toggledState = false,
        setToggledState = () => null,
        offCanvas = false
    ) => {
        if (!user)
            return null

        const offCanvasMenuLabelId = idPrefix
            && offCanvas
            && `${idPrefix}-menu-label`

        const onUserMenuItemClick = () => {
            if (!stickyOverlay)
                return setToggledState(false)

            setStickyOverlay(false)
            setTimeout(() => setToggledState(false), 1)
        }

        const onUserMenuToggle = () => {
            const isOpenOffCanvas = !toggledState
                && window.innerWidth < 1024

            if (isOpenOffCanvas
                    && !scrollLock)
                toggleScrollLock()

            setToggledState(!toggledState)

            if (!isOpenOffCanvas
                    || !offCanvasMenuLabelId)
                return

            const focusEl = document
                .getElementById(offCanvasMenuLabelId)

            if (focusEl)
                setTimeout(() => {
                    focusEl.focus()
                }, 1)
        }

        const renderUserAvatar = (sm = false) => (
            <span
                className={`avatar${
                    sm
                        ? ' avatar-sm'
                        : ''}${
                    user.avatar_url
                        ? ' avatar-img'
                        : ''}`}
            >
                {!user.avatar_url
                        || user.avatar_url.match(/gravatar\.com/)
                    ? <span>{authProvider.getInitials(user.display_name)}</span>
                    : (
                        <Image
                            src={`${user.avatar_url}?w=128`}
                            alt={`Avatar of ${user.display_name}`}
                            data-object-fit="cover"
                        />
                    )}
            </span>
        )

        return (
            <div
                className="global-header-user col-auto"
            >
                {advancedMembershipAt
                    && isDashboard
                    && (
                        <div className="margin-1-r display-none-lg-down">
                            {(() => {
                                const date = typeof advancedMembershipAt
                                        === 'string'
                                    && (new Date(advancedMembershipAt))
                                        .toLocaleDateString('en-US', {
                                            month: 'long',
                                            year: 'numeric'
                                        })

                                const text = (
                                    <span
                                        className="body-5"
                                        style={{
                                            color: '#fff',
                                            fontWeight: 500,
                                            cursor: 'default'
                                        }}
                                    >
                                        Advanced Member
                                    </span>
                                )

                                if (!date)
                                    return text

                                return (
                                    <Tippy
                                        className="tippy-light"
                                        content={`Since ${date}`}
                                    >
                                        {text}
                                    </Tippy>
                                )
                            })()}
                        </div>
                    )}
                <button
                    id={idPrefix
                        && `${idPrefix}-toggle`}
                    className="btn-unstyled"
                    aria-expanded={toggledState}
                    aria-controls={idPrefix
                        && `${idPrefix}-menu`}
                    aria-label={`${toggledState
                        ? 'Close'
                        : 'Open'} user account menu`}
                    aria-hidden={window.innerWidth < 1024
                        && userMenuStickyToggled}
                    onClick={onUserMenuToggle}
                    ref={toggleRef}
                >
                    {renderUserAvatar(true)}
                </button>
                {offCanvas
                    && (
                        <div
                            className="global-header-user-menu-bg"
                            data-expanded={toggledState}
                        />)}
                <div
                    id={idPrefix
                        && `${idPrefix}-menu`}
                    className="global-header-user-menu"
                    ref={menuRef}
                    data-expanded={toggledState}
                    aria-labelledby={offCanvasMenuLabelId}
                >
                    {offCanvas
                        && (
                            <div className="margin-b display-none-lg-up">
                                <div className="row flex-justify-content-space-between margin-1-b">
                                    <div className="col-auto">
                                        {renderUserAvatar()}
                                    </div>
                                    <div className="col-auto">
                                        <button
                                            className="btn-unstyled margin-1-t"
                                            onClick={() =>
                                                setToggledState(false)}
                                        >
                                            <svg
                                                className="icon-close"
                                                width="18"
                                                height="18"
                                            >
                                                <use xlinkHref="#icon-close" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <h2
                                    id={offCanvasMenuLabelId
                                        && offCanvasMenuLabelId}
                                    className="text-screen-reader"
                                    tabIndex="-1"
                                >
                                    Account menu
                                </h2>
                                <h3
                                    className="heading-6 font-weight-medium"
                                >
                                    {authProvider.getDisplayName(true)}
                                </h3>
                            </div>
                        )}
                    <ul>
                        {(authProvider.getDefaultRole() || '')
                            .match(/^(advisor|member)$/)
                            && (
                                <li>
                                    <Link
                                        to={`${dashboardPath}/profiles/${
                                            user.id}`}
                                        className="body-3"
                                        onClick={onUserMenuItemClick}
                                    >
                                        My profile
                                    </Link>
                                </li>)}
                        <li>
                            <Link
                                to={`${dashboardPath}/settings`}
                                className="body-3"
                                onClick={onUserMenuItemClick}
                            >
                                Settings
                            </Link>
                        </li>
                        <li>
                            <button
                                className="btn-unstyled"
                                onClick={() => {
                                    onUserMenuItemClick()

                                    authProvider.logout()
                                        .then(() => {
                                            setUser(false)
                                        })
                                }}
                            >
                                Log out
                            </button>
                        </li>
                    </ul>
                    <div className="margin-t">
                        {!isDashboard
                            && (
                                <Link
                                    to={dashboardPath}
                                    className="btn pad-1-l-lg-down pad-1-r-lg-down margin-2-b"
                                    onClick={onUserMenuItemClick}
                                >
                                    View Dashboard
                                </Link>)}
                        <Link
                            to="/privacy-terms"
                            className="body-4"
                            onClick={onUserMenuItemClick}
                        >
                            Privacy & Terms
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    const renderDashboardMenu = (stickyOverlayChild = false) => {
        if (!user)
            return null

        const dashboardBasePath = dashboardPath
            .replace(/\/$/, '')

        const isDashboardHome = pathname === dashboardBasePath
            || pathname === `${dashboardBasePath}/`

        const isDashboardHub = Boolean((pathname
            .match(new RegExp(`^${dashboardBasePath
                .replace(/\//g, '\\/')}\\/hub`)) || {})
            .length || 0)

        const isDashboardSettings = Boolean((pathname
            .match(new RegExp(`^${dashboardBasePath
                .replace(/\//g, '\\/')}\\/settings`)) || {})
            .length || 0)

        const onDashboardMenuItemClick = () => stickyOverlayChild
            && stickyOverlay
            && setStickyOverlay(false)

        const isAsideTooltips = !stickyOverlayChild
            && window.innerWidth >= 1024
            && window.innerWidth < 1440

        const DashboardMenuLink = (props) => isAsideTooltips
                && (props.content
                    || props.title)
            ? (
                <Tippy
                    content={props.content || props.title}
                    singleton={tippyTarget}
                >
                    {props.children}
                </Tippy>)
            : (
                <>
                    {props.children}
                </>)

        return (
            <ul className="global-header-dashboard-menu">
                <li
                    className={`global-header-dashboard-menu-item${
                        isDashboardHome
                            ? ' global-header-dashboard-menu-item-active'
                            : ''}`}
                >
                    <DashboardMenuLink title="Dashboard">
                        <Link
                            to={dashboardBasePath}
                            aria-current={isDashboardHome
                                && 'page'}
                            onClick={onDashboardMenuItemClick}
                        >
                            <svg
                                className="icon-dashboard flex-none"
                                width="20"
                                height="20"
                                aria-hidden="true"
                            >
                                <use xlinkHref="#icon-dashboard" />
                            </svg>
                            <span>Dashboard</span>
                        </Link>
                    </DashboardMenuLink>
                </li>
                <li
                    className={`global-header-dashboard-menu-item${
                        isDashboardHub
                            ? ' global-header-dashboard-menu-item-active'
                            : ''}`}
                >
                    <DashboardMenuLink title="Content hub">
                        <Link
                            to={`${dashboardBasePath}/hub`}
                            aria-current={isDashboardHub
                                && 'page'}
                            onClick={onDashboardMenuItemClick}
                        >
                            <svg
                                className="icon-workspaces flex-none"
                                width="20"
                                height="20"
                                aria-hidden="true"
                            >
                                <use xlinkHref="#icon-workspaces" />
                            </svg>
                            <span>Content hub</span>
                        </Link>
                    </DashboardMenuLink>
                </li>
                {(foundertracAccessToken
                        || advancedMembershipAt)
                    && (
                        <li
                            className="global-header-dashboard-menu-item"
                        >
                            <DashboardMenuLink
                                content={foundertracAccessToken
                                    && (
                                        <div className="display-flex flex-justify-content-center">
                                            <span>FounderTrac</span>
                                            <svg
                                                className="icon-launch"
                                                width="14"
                                                height="14"
                                                style={{
                                                    marginLeft: 3
                                                }}
                                            >
                                                <use xlinkHref="#icon-launch" />
                                            </svg>
                                        </div>)}
                                title="FounderTrac"
                            >
                                {foundertracAccessToken
                                    ? (
                                        <a
                                            href={`${window.FOUNDERTRAC_URL}/authorize/${
                                                window.FOUNDERTRAC_CLIENT_ID}?a=${
                                                foundertracAccessToken}`}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            title="Opens in a new tab"
                                        >
                                            <svg
                                                className="icon-route flex-none"
                                                width="20"
                                                height="20"
                                                aria-hidden="true"
                                            >
                                                <use xlinkHref="#icon-route" />
                                            </svg>
                                            <span>FounderTrac</span>
                                            <svg
                                                className="icon-launch"
                                                width="14"
                                                height="14"
                                            >
                                                <use xlinkHref="#icon-launch" />
                                            </svg>
                                        </a>)
                                    : (
                                        <button
                                            className="btn-unstyled"
                                            onClick={() => setModal({
                                                'aria-labelledby': 'modal-heading',
                                                className: 'modal-foundertrac-registration',
                                                size: 'lg',
                                                dialogScrollable: true,
                                                icon: 'icon-like',
                                                iconBackground: getCssVar('--aqua'),
                                                component: (
                                                    <FounderTracRegistration
                                                        profile={{
                                                            email: user.email
                                                        }}
                                                    />
                                                )
                                            })}
                                        >
                                            <svg
                                                className="icon-route flex-none"
                                                width="20"
                                                height="20"
                                                aria-hidden="true"
                                            >
                                                <use xlinkHref="#icon-route" />
                                            </svg>
                                            <span>FounderTrac</span>
                                        </button>
                                    )}
                            </DashboardMenuLink>
                        </li>)}
                <li
                    className={`global-header-dashboard-menu-item global-header-dashboard-menu-item-settings${
                        isDashboardSettings
                            ? ' global-header-dashboard-menu-item-active'
                            : ''}`}
                >
                    <DashboardMenuLink title="Settings">
                        <Link
                            to={`${dashboardBasePath}/settings`}
                            aria-current={isDashboardSettings
                                && 'page'}
                            onClick={onDashboardMenuItemClick}
                        >
                            <svg
                                className="icon-settings flex-none"
                                width="20"
                                height="20"
                                aria-hidden="true"
                            >
                                <use xlinkHref="#icon-settings" />
                            </svg>
                            <span>Settings</span>
                        </Link>
                    </DashboardMenuLink>
                </li>
            </ul>
        )
    }

    return (
        <header
            ref={onHeaderRefCallback}
            id="global-header"
            className={`global-header${
                isDashboard
                    ? ' global-header-dashboard'
                    : ''} ${props.className || ''}`
                .trim()}
            style={props.style}
            data-sticky={sticky || false}
            data-sticky-overlay={stickyOverlay && scrollLock}
            data-sticky-disabled={stickyDisabled}
        >
            <nav
                className={`global-header-primary container display-none${
                    !isDashboard
                        ? '-lg-down'
                        : ''}`}
                aria-label="Primary"
            >
                <div className="row flex-justify-content-space-between">
                    <div className="col display-flex">
                        <Link
                            className="global-header-logo"
                            to="/"
                            aria-label="Catapult Homepage"
                        >
                            <svg
                                className="logo-catapult"
                                width="1246"
                                height="315"
                            >
                                <use xlinkHref="#logo-catapult" />
                            </svg>
                        </Link>
                    </div>
                    <div className="col body-3 font-weight-normal">
                        <div className="row-condensed flex-align-items-center flex-justify-content-end">
                            {!user
                                && (
                                    <div className="col-auto">
                                        <Link to="/login">
                                            Login
                                        </Link>
                                    </div>)}
                            {!isDashboard
                                && (
                                    <div className="col-auto">
                                        <a
                                            className="flex-align-items-center"
                                            href="https://www.gse.upenn.edu/about-penn-gse/catalyst"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Opens in new tab"
                                        >
                                            <span>Catalyst @ Penn GSE</span>
                                            <svg
                                                className="icon-launch flex-none"
                                                width="14"
                                                height="14"
                                            >
                                                <use xlinkHref="#icon-launch" />
                                            </svg>
                                        </a>
                                    </div>)}
                            {renderUser(
                                'global-header-user-primary',
                                userMenuPrimaryToggle,
                                userMenuPrimary,
                                userMenuPrimaryToggled,
                                setUserMenuPrimaryToggled)}
                        </div>
                    </div>
                </div>
            </nav>
            <nav
                id="global-header-sticky"
                className="global-header-sticky"
                aria-label={`${isDashboard
                    ? 'User'
                    : (stickyOverlay && scrollLock
                        ? 'Menu'
                        : 'Primary')}`}
                aria-modal={(stickyOverlay
                    && scrollLock
                    && 'dialog') || null}
                role={(stickyOverlay
                    && scrollLock
                    && 'dialog') || null}
                ref={stickyNav}
            >
                <div
                    id="global-header-sticky-bar"
                    className="global-header-sticky-bar"
                    ref={stickyBar}
                >
                    <div
                        className={`container${isDashboard
                                && window.innerWidth >= 1024
                            ? '-fluid pad-3-l-xxl-up pad-3-r-xxl-up'
                            : ''}`}
                    >
                        <div className="row flex-nowrap">
                            <div className="col display-inline-flex flex-align-items-center display-none-lg-up">
                                <button
                                    id="global-header-sticky-overlay-toggle"
                                    className="global-header-sticky-overlay-toggle btn-unstyled"
                                    aria-controls="global-header-sticky-overlay-menu"
                                    aria-expanded={stickyOverlay}
                                    aria-label={`${stickyOverlay && scrollLock
                                        ? 'Hide'
                                        : 'Show'} Navigation Menu`}
                                    aria-hidden={window.innerWidth < 1024
                                        && userMenuStickyToggled}
                                    onClick={() => setStickyOverlay(!stickyOverlay)}
                                    ref={stickyOverlayToggle}
                                >
                                    <span className="global-header-sticky-overlay-toggle-bar" />
                                    <span className="global-header-sticky-overlay-toggle-bar" />
                                    <span className="global-header-sticky-overlay-toggle-bar" />
                                </button>
                            </div>
                            <div
                                className="col-auto display-inline-flex flex-align-items-center"
                            >
                                <Link
                                    className="global-header-sticky-logo"
                                    to={isDashboard
                                        ? dashboardPath
                                        : '/'}
                                    aria-label={`Catapult ${isDashboard
                                        ? 'Dashboard'
                                        : 'Homepage'}`}
                                    aria-hidden={window.innerWidth < 1024
                                        && userMenuStickyToggled}
                                    onClick={() => stickyOverlay
                                        && scrollLock
                                        && setStickyOverlay(!stickyOverlay)}
                                >
                                    <svg
                                        className="logo-catapult-condensed"
                                        width="1102"
                                        height="310"
                                    >
                                        <use xlinkHref="#logo-catapult-condensed" />
                                    </svg>
                                </Link>
                            </div>
                            <div className="col display-flex flex-align-items-center">
                                <div className="row-condensed flex-justify-content-end flex-grow">
                                    {!user
                                        && (
                                            <div className="col-auto display-inline-flex flex-align-items-center display-none-lg-down">
                                                <Link
                                                    to="/login"
                                                    onClick={() => stickyOverlay
                                                        && scrollLock
                                                        && setStickyOverlay(!stickyOverlay)}
                                                >
                                                    Login
                                                </Link>
                                            </div>)}
                                    {!isDashboard
                                        && (
                                            <div className="col-auto display-inline-flex flex-align-items-center display-none-lg-down">
                                                <a
                                                    className="flex-align-items-center"
                                                    href="https://www.gse.upenn.edu/about-penn-gse/catalyst"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="Opens in new tab"
                                                >
                                                    <span>Catalyst @ Penn GSE</span>
                                                    <svg
                                                        className="icon-launch flex-none"
                                                        width="14"
                                                        height="14"
                                                    >
                                                        <use xlinkHref="#icon-launch" />
                                                    </svg>
                                                </a>
                                            </div>)}
                                    {!user
                                        && stickyCtaText
                                        && props.stickyCta.href
                                        && (
                                            <div
                                                className={`global-header-sticky-cta col-auto display-inline-flex flex-align-items-center${
                                                    !stickyCtaTextSm
                                                        ? ' display-none-lg-down'
                                                        : ''}`}
                                            >
                                                <Link
                                                    className="btn"
                                                    to={props.stickyCta.href}
                                                    onClick={() => stickyOverlay
                                                        && scrollLock
                                                        && setStickyOverlay(!stickyOverlay)}
                                                >
                                                    <span className="display-none-lg-up">
                                                        {stickyCtaTextSm}
                                                        {stickyCtaTextSm !== stickyCtaText
                                                            && (
                                                                <span className="text-screen-reader">
                                                                    {stickyCtaTextWords
                                                                        .slice(1)
                                                                        .join(' ')}
                                                                </span>
                                                            )}
                                                    </span>
                                                    <span
                                                        className="display-none-lg-down"
                                                    >
                                                        {stickyCtaText}
                                                    </span>
                                                </Link>
                                            </div>)}
                                    {renderUser(
                                        'global-header-user-sticky',
                                        userMenuStickyToggle,
                                        userMenuSticky,
                                        userMenuStickyToggled,
                                        setUserMenuStickyToggled,
                                        true)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className="global-header-sticky-overlay-menu display-flex flex-direction-column"
                    id="global-header-sticky-overlay-menu"
                    aria-hidden={window.innerWidth < 1024
                        && userMenuStickyToggled}
                >
                    <div className="container flex-grow">
                        {renderDashboardMenu(true)}
                        <ul className="display-flex flex-direction-column pad-5-b margin-2-b-neg">
                            {!user
                                && (
                                    <li className="margin-b">
                                        <a
                                            className="flex-align-items-center body-3"
                                            href="https://www.gse.upenn.edu/about-penn-gse/catalyst"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Opens in new tab"
                                        >
                                            <span>Catalyst @ Penn GSE</span>
                                            <svg
                                                className="icon-launch flex-none"
                                                width="14"
                                                height="14"
                                            >
                                                <use xlinkHref="#icon-launch" />
                                            </svg>
                                        </a>
                                    </li>)}
                            {!user
                                && (
                                    <li className="display-flex margin-b">
                                        <Link
                                            to="/login"
                                            className="btn-lg flex-grow body"
                                            onClick={() => stickyOverlay
                                                && scrollLock
                                                && setStickyOverlay(!stickyOverlay)}
                                        >
                                            Login
                                        </Link>
                                    </li>)}
                            {!user
                                && stickyCtaText
                                && props.stickyCta.href
                                && (
                                    <li className="display-flex margin-b">
                                        <Link
                                            to={props.stickyCta.href}
                                            className="btn-lg flex-grow body"
                                            onClick={() => stickyOverlay
                                                && scrollLock
                                                && setStickyOverlay(!stickyOverlay)}
                                        >
                                            {stickyCtaText}
                                        </Link>
                                    </li>)}
                            {user
                                && (
                                    <li className="display-flex">
                                        <a
                                            className="global-header-sticky-overlay-menu-logo-catalyst logo-catalyst"
                                            href="https://www.gse.upenn.edu/about-penn-gse/catalyst"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Opens in new tab"
                                            aria-label="Catalyst @ Penn GSE"
                                        >
                                            <svg
                                                className="logo-catalyst-penn-gse"
                                                width="286"
                                                height="50"
                                            >
                                                <use xlinkHref="#logo-catalyst-penn-gse" />
                                            </svg>
                                        </a>
                                    </li>)}
                        </ul>
                    </div>
                </div>
            </nav>
            {isDashboard
                && (
                    <>
                        <nav
                            id="global-header-dashboard-aside"
                            className="global-header-dashboard-aside display-none-lg-down"
                            aria-label="Primary"
                            ref={dashboardAside}
                        >
                            <div className="display-flex flex-grow flex-direction-column pad-4-t pad-4-b">
                                {renderDashboardMenu()}
                                <a
                                    className="global-header-dashboard-aside-logo-catalyst logo-catalyst"
                                    href="https://www.gse.upenn.edu/about-penn-gse/catalyst"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Opens in new tab"
                                    aria-label="Catalyst @ Penn GSE"
                                >
                                    <svg
                                        className="logo-catalyst-penn-gse-condensed display-block display-none-xxl-up"
                                        width="30"
                                        height="30"
                                    >
                                        <use xlinkHref="#logo-catalyst-penn-gse-condensed" />
                                    </svg>
                                    <svg
                                        className="logo-catalyst-penn-gse display-none display-block-xxl-up"
                                        width="143"
                                        height="27"
                                    >
                                        <use xlinkHref="#logo-catalyst-penn-gse" />
                                    </svg>
                                </a>
                            </div>
                        </nav>
                    </>)}
            <Tippy
                singleton={tippySource}
                delay={0}
                moveTransition="transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)"
                placement="right"
                disabled={!isDashboard
                    || window.innerWidth < 1024
                    || window.innerWidth >= 1440}
            />
        </header>
    )
}
